import { Injectable } from '@angular/core';
import { LoaderService } from './loader.service';
import { NotificationService } from './notification.service';
import { ParsingUtilities } from 'src/app/helpers/parsing-utilities';
import {
  HTTP_ERROR_MESSAGES,
  HTTP_ERROR_TITLES,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_ERROR_TITLE,
  CONSTRAINT_VIOLATION_MESSAGE,
  CONSTRAINT_VIOLATION_PATTERNS,
  sanitizeBackendMessage
} from '../constants/http-error-messages';
import { SalaryValidationErrorService } from './salary-validation-error.service';
import { SessionExpiredService } from './session-expired.service';

/**
 * Ventana de supresión: si llega un error con la misma firma (status+mensaje)
 * dentro de este intervalo, se suprime el popup duplicado. Se eligen 30s porque
 * cubre el caso típico de un backend caído donde múltiples peticiones simultáneas
 * o reintentos rápidos generan N errores idénticos.
 */
const DEDUP_WINDOW_MS = 30_000;

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  /** Cache de firmas de error recientes para evitar alertas duplicadas. */
  private readonly recentErrorKeys = new Map<string, number>();

  constructor(
    private readonly loaderService: LoaderService,
    private readonly notification: NotificationService,
    private readonly salaryValidationErrorService: SalaryValidationErrorService,
    private readonly sessionExpired: SessionExpiredService
  ) {}

  public handleError(error: any): void {
    const rawMessage: string = ParsingUtilities.getErrorDetail(error).toString();
    const lowerRawMessage: string = rawMessage.toLowerCase();

    // Detectar constraint violation de base de datos (siempre se muestra — relevante para el usuario)
    const isConstraintViolation = CONSTRAINT_VIOLATION_PATTERNS
      .some(pattern => lowerRawMessage.includes(pattern));

    if (isConstraintViolation) {
      this.notification.alert('No se puede eliminar', CONSTRAINT_VIOLATION_MESSAGE, 'warning');
      this.loaderService.hide();
      return;
    }

    if (!error) {
      this.notification.alert(DEFAULT_ERROR_TITLE, DEFAULT_ERROR_MESSAGE, 'error');
      this.loaderService.hide();
      return;
    }

    // Dedup: si la misma firma ya se notificó hace poco, ocultar loader y salir en silencio.
    // Las validaciones de constraint y error null nunca caen aquí (early-return arriba).
    if (this.isDuplicateError(error, rawMessage)) {
      this.loaderService.hide();
      return;
    }

    switch (error.status) {
      case 401:
        // Defensa en profundidad: el AuthInterceptor ya invoca SessionExpiredService.
        // Si un 401 logra llegar acá (skipHandler=false en algún caso particular)
        // delegamos al mismo servicio para garantizar comportamiento consistente.
        this.sessionExpired.handle('UNAUTHENTICATED', error);
        break;

      case 400: {
        // Para 400, intentar mostrar un mensaje sanitizado del backend
        const sanitized = sanitizeBackendMessage(rawMessage);
        const message = sanitized || HTTP_ERROR_MESSAGES[400] || DEFAULT_ERROR_MESSAGE;
        this.notification.alert(HTTP_ERROR_TITLES[400], message, 'warning');
        break;
      }

      case 422: {
        if (this.salaryValidationErrorService.isSalaryValidationError(error)) {
          this.salaryValidationErrorService.handleSalaryValidationError(error);
        } else {
          const message = HTTP_ERROR_MESSAGES[422] || DEFAULT_ERROR_MESSAGE;
          this.notification.alert(HTTP_ERROR_TITLES[422], message, 'warning');
        }
        break;
      }

      default: {
        // Para otros status codes, usar el mapa de mensajes amigables
        // NUNCA exponer detalles técnicos del backend para 500+
        const message = HTTP_ERROR_MESSAGES[error.status] || DEFAULT_ERROR_MESSAGE;
        const title = HTTP_ERROR_TITLES[error.status] || DEFAULT_ERROR_TITLE;
        const type = error.status >= 500 ? 'error' : 'warning';
        this.notification.alert(title, message, type);
        break;
      }
    }

    this.loaderService.hide();
  }

  /**
   * Llamado por el ErrorInterceptor cuando una petición HTTP responde con éxito.
   * Indica que el backend está vivo otra vez → limpiamos el cache de dedup
   * para que el próximo fallo (si lo hay) sí notifique al usuario.
   */
  public notifyResponseOk(): void {
    if (this.recentErrorKeys.size > 0) {
      this.recentErrorKeys.clear();
    }
  }

  private isDuplicateError(error: any, rawMessage: string): boolean {
    const key = this.buildDedupKey(error, rawMessage);
    const now = Date.now();
    const last = this.recentErrorKeys.get(key);
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
      // Refrescamos timestamp para mantener silencio mientras siga el aluvión
      this.recentErrorKeys.set(key, now);
      return true;
    }
    this.recentErrorKeys.set(key, now);
    return false;
  }

  private buildDedupKey(error: any, rawMessage: string): string {
    const status = error?.status ?? 'unknown';
    // Prefijo corto del mensaje para distinguir errores diferentes del mismo status
    const msgKey = (rawMessage || '').slice(0, 80).toLowerCase();
    return `${status}::${msgKey}`;
  }
}
